/**
 * ISRC (ISO 3901:2019): 12 caracteres, sin dígito verificador.
 *
 *   AR F03 97 03368  →  agencia (2 letras) · registrante (3 letras o números)
 *                       · año de referencia (2 números) · designación (5 números)
 *
 * Se guarda la forma normativa de 12 caracteres (ARF039703368): la unicidad por
 * tenant y las exportaciones comparan esa forma. Los guiones y el literal
 * "ISRC" son de presentación (ISRC Handbook §5) y se quitan.
 *
 * ESPEJO byte-idéntico: jupiter-server/src/common/validation/identifiers/isrc.ts
 * y jupiter-client/src/entities/common/validation/identifiers/isrc.ts
 */

import type { IdentifierCheck, IdentifierIssue } from "./index";
import { IFPI_AGENCY_CODES, IFPI_AGENCY_CODES_DATE } from "./ifpiAgencyCodes";

/** Espacios (con NBSP), guiones Unicode (U+2010–U+2015, U+2212) y caracteres de ancho cero. */
const SEPARATORS = /[\s\u200B-\u200D\u2060\uFEFF\u2010-\u2015\u2212-]/g;

const ISRC_PATTERN = /^[A-Z]{2}[A-Z0-9]{3}[0-9]{7}$/;

/** Prefijos de ejemplo del Bulletin 2015/01 de la IFPI: no se asignan nunca. */
const RESERVED_PREFIXES = ["USS1Z", "JMK40"];

/** Solo a–z: `toUpperCase()` general convierte `ı` en `I` y `ß` en `SS`. */
function upperAsciiLetters(text: string): string {
  return text.replace(/[a-z]/g, (c) => c.toUpperCase());
}

function formatError(value: string): IdentifierIssue {
  if (/^[0-9]{12,13}$/.test(value)) {
    return {
      code: "ISRC_LOOKS_LIKE_UPC",
      message:
        "Son solo números: parece un código de barras (UPC/EAN), no un ISRC. El ISRC empieza con 2 letras, por ejemplo AR-F03-97-03368.",
    };
  }
  if (/^[A-Z]{2}[A-Z0-9]{3}[0-9OI]{7}$/.test(value)) {
    const tail = value.slice(5);
    const hints: string[] = [];
    if (tail.includes("O")) hints.push("la O es un cero");
    if (tail.includes("I")) hints.push("la I es un uno");
    return {
      code: "ISRC_LETTER_IN_DIGITS",
      message: `Las posiciones 6 a 12 llevan solo números: ¿${hints.join(" y ")}?`,
    };
  }
  if (value.length === 11) {
    return {
      code: "ISRC_MISSING_DIGIT",
      message:
        "Tiene 11 caracteres y el ISRC lleva 12: ¿falta un cero? El número final siempre tiene 5 dígitos (00047, no 47).",
    };
  }
  return {
    code: "ISRC_FORMAT",
    message:
      "El ISRC son 12 caracteres: 2 letras, 3 letras o números y 7 números (por ejemplo AR-F03-97-03368).",
  };
}

export function checkIsrc(raw: string | null | undefined): IdentifierCheck {
  if (raw === null || raw === undefined) return { value: null };
  const trimmed = String(raw).normalize("NFKC").trim();
  if (trimmed === "") return { value: null };

  // El literal "ISRC" se quita solo si deja 12 caracteres: ISRCA2000001 es un
  // ISRC válido de Islandia (agencia IS, registrante RCA).
  const value = upperAsciiLetters(trimmed)
    .replace(SEPARATORS, "")
    .replace(/^ISRC:?(?=[A-Z0-9]{12}$)/, "");

  // Si no queda nada al normalizar, el valor es lo que se escribió: nunca "" con error.
  if (!ISRC_PATTERN.test(value)) return { value: value || trimmed, error: formatError(value) };

  const prefix = value.slice(0, 5);
  if (RESERVED_PREFIXES.includes(prefix)) {
    return {
      value,
      error: {
        code: "ISRC_RESERVED_PREFIX",
        message: `El prefijo ${prefix} es un ejemplo de la IFPI y nunca se asigna a nadie.`,
      },
    };
  }

  const agency = value.slice(0, 2);
  if (agency === "TC" && Number(value.slice(5, 7)) >= 26) {
    return {
      value,
      error: {
        code: "ISRC_TC_RETIRED",
        message:
          "El código de agencia TC (el que usaba TuneCore) no se puede usar con año de referencia 26 o posterior.",
      },
    };
  }

  if (!IFPI_AGENCY_CODES.has(agency)) {
    return {
      value,
      warning: {
        code: "ISRC_UNKNOWN_AGENCY",
        message: `El código de agencia ${agency} no está en la lista de la IFPI (${IFPI_AGENCY_CODES_DATE}). Revisá el ISRC; si el código es nuevo, se puede guardar igual.`,
      },
    };
  }

  return { value };
}
